import React, { useMemo, useReducer, useState, useEffect } from "react";
import ShippingTable from "../../elements/Tables/ShippingTable/ShippingTable";
// import ShippingCreationForm from "../../elements/Forms/ShippingCreationForm/ShippingCreationForm";

import { useFetch } from "../../../Utils/useFetch";

import {
  FETCH_ALL_SHIPPING_API,
  FETCH_ALL_STOCK_API,
  FETCH_ALL_COUNTER_API,
} from "../../../Constants/Apis";
import { Button, Select, Input, message, Modal, DatePicker } from "antd";
import { reducer } from "../../../Utils/CommonUtils";
import { deleteShipping } from "../../../Services/ShippingService";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { Search } = Input;
const { RangePicker } = DatePicker;

const normalize = (value) => String(value ?? "").trim().toLowerCase();

const includesText = (haystack, needle) => {
  if (!needle) return true;
  return normalize(haystack).includes(normalize(needle));
};

const matchesAny = (value, selectedList) => {
  if (!Array.isArray(selectedList) || selectedList.length === 0) return true;
  return selectedList.includes(value);
};

const toMs = (v) => {
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

const ShippingPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {
      tanggal_kirim: {
        startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
      },
    },
    sorter: {},
    current: 1,
    pageSize: 10,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shippingId, setShippingId] = useState(null);
  const [selectedNoSJ, setSelectedNoSJ] = useState(null);

  // Separate pagination state for stock table
  const [stockPagination, setStockPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handleOpenModal = () => setIsModalVisible(true);
  const handleCloseModal = () => setIsModalVisible(false);

  const [senderFilter, setSenderFilter] = useState([]);
  const [destinationFilter, setDestinationFilter] = useState([]);
  const [shippingTypeFilter, setShippingTypeFilter] = useState(undefined);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"),
    dayjs()
  ]);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  // Store all available options for dropdowns separately
  const [senderOptions, setSenderOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);

  const handleSenderFilter = (value) => {
    // For Regular shipping, sender must always be GP and cannot be changed
    if (shippingTypeFilter === "Regular") {
      return;
    }

    setSenderFilter(value);
    dispatch({
      type: "filteredInfo",
      payload: {
        ...state.filteredInfo,
        sender: value && value.length > 0 ? value : undefined,
      },
    });
    dispatch({ type: "current", payload: 1 });
  };

  const handleDestinationFilter = (value) => {
    setDestinationFilter(value);
    dispatch({
      type: "filteredInfo",
      payload: {
        ...state.filteredInfo,
        destination: value && value.length > 0 ? value : undefined,
      },
    });
    dispatch({ type: "current", payload: 1 });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);

    let newFilteredInfo = { ...state.filteredInfo };

    if (dates && dates[0] && dates[1]) {
      newFilteredInfo.tanggal_kirim = {
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      };
    } else {
      delete newFilteredInfo.tanggal_kirim;
    }

    dispatch({
      type: "filteredInfo",
      payload: newFilteredInfo,
    });

    dispatch({ type: "current", payload: 1 });
  };

  const handleShippingTypeFilter = (value) => {
    // First update the shipping type filter UI state
    setShippingTypeFilter(value);

    // Create a new filteredInfo object starting with existing filters
    let newFilteredInfo = { ...state.filteredInfo };

    // Handle shipping type filter
    if (value) {
      // Don't add shippingType to filters since it doesn't exist in the model
      // Only use it to control sender and destination filters

      // Apply conditional filters based on shipping type
      if (value === "Return") {
        // For Return: destination is GP
        newFilteredInfo.destination = ["GP"];
        delete newFilteredInfo.sender; // Clear sender filter

        // Update UI state for dropdowns
        setDestinationFilter(["GP"]);
        setSenderFilter([]);
      } else if (value === "Regular") {
        // For Regular: sender is GP
        newFilteredInfo.sender = ["GP"];
        delete newFilteredInfo.destination; // Clear destination filter

        // Update UI state for dropdowns
        setSenderFilter(["GP"]);
        setDestinationFilter([]);
      } else if (value === "Mutasi") {
        // For Mutasi: select all in sender and destination except for 'GP'
        const sendersExceptGP = senderOptions.filter((sender) => sender !== "GP");
        const destinationsExceptGP = destinationOptions.filter(
          (destination) => destination !== "GP"
        );

        newFilteredInfo.sender = sendersExceptGP;
        newFilteredInfo.destination = destinationsExceptGP;

        // Update UI state for dropdowns
        setSenderFilter(sendersExceptGP);
        setDestinationFilter(destinationsExceptGP);
      } else if (value === "Return Pabrik") {
        // For Return Pabrik: select all in sender except for 'GP', and select 'PRI' in destination
        const sendersExceptGP = senderOptions.filter((sender) => sender !== "GP");

        newFilteredInfo.sender = sendersExceptGP;
        newFilteredInfo.destination = ["PRI"];

        // Update UI state for dropdowns
        setSenderFilter(sendersExceptGP);
        setDestinationFilter(["PRI"]);
      }
    } else {
      // If no shipping type is selected, clear conditional filters
      delete newFilteredInfo.sender;
      delete newFilteredInfo.destination;

      // Reset UI state for dropdowns
      setSenderFilter([]);
      setDestinationFilter([]);
    }

    // Update the filteredInfo state
    dispatch({
      type: "filteredInfo",
      payload: newFilteredInfo,
    });

    // Reset to first page when changing filters
    dispatch({ type: "current", payload: 1 });

    // Fetch data with the updated filters
  };

  const handleDelete = async (id) => {
    setShippingId(id);
    setIsModalOpen(true);
  };

  const handleCreate = async () => {
    history.push("/user/create-shipping");
  };

  const handleOk = async () => {
    try {
      await deleteShipping(shippingId);
      message.success("Shipping deleted successfully");
      callReFetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong, failed to delete shipping."
      );
    }
    setIsModalOpen(false);
  };

  // Always use the /all endpoint and pass user counter code for filtering.
  // We fetch a large page and paginate + filter locally to ensure UI filters work.
  const shippingApiUrl = `${FETCH_ALL_SHIPPING_API}/all`;

  const serverFilteredInfo = useMemo(() => {
    const filteredInfo = {};

    if (state.filteredInfo?.tanggal_kirim) {
      filteredInfo.tanggal_kirim = state.filteredInfo.tanggal_kirim;
    }

    return filteredInfo;
  }, [state.filteredInfo]);

  const counterParam = useMemo(() => {
    const codes = new Set();

    if (userData?.counterCode) {
      codes.add(userData.counterCode);
    }

    const senderCodes = state.filteredInfo?.sender || [];
    const destinationCodes = state.filteredInfo?.destination || [];

    senderCodes.forEach((code) => codes.add(code));
    destinationCodes.forEach((code) => codes.add(code));

    return Array.from(codes).join(",");
  }, [state.filteredInfo, userData?.counterCode]);

  const shippingQueryParams = useMemo(() => {
    return {
      counterCode: counterParam,
      current: 1,
      pageSize: 50000,
      searchKeyword: "",
      sorter: {},
      filteredInfo: serverFilteredInfo,
    };
  }, [counterParam, serverFilteredInfo]);

  // Fetch all shipping data (date-filtered server-side)
  const { fetchedData, callReFetch, loading } = useFetch(
    shippingApiUrl,
    shippingQueryParams
  );

  useEffect(() => {
    callReFetch();
  }, [counterParam, serverFilteredInfo]);

  // Fetch all counters once for dropdown options (sender / destination)
  const { fetchedData: countersFetched = {} } = useFetch(
    FETCH_ALL_COUNTER_API,
    {}
  );

  const allShippingRows = fetchedData?.data?.data || [];

  const {
    fetchedData: stockFetched = { data: {} },
    callReFetch: callReFetchStock,
    loading: loadingStock,
  } = useFetch(
    selectedNoSJ ? `${FETCH_ALL_STOCK_API}/sj/${encodeURIComponent(selectedNoSJ)}` : null,
    {
      current: 1,
      pageSize: 50000,
    }
  );

  useEffect(() => {
    if (selectedNoSJ) {
      // Reset stock pagination to first page when selecting a new shipping
      setStockPagination((prev) => ({
        ...prev,
        current: 1,
      }));
    }
  }, [selectedNoSJ]);

  // Separate effect to trigger fetch when selectedNoSJ changes
  // This ensures the URL is updated in useFetch before triggering the fetch
  useEffect(() => {
    if (selectedNoSJ) {
      // Use requestAnimationFrame to ensure URL is updated in useFetch hook
      const timer = setTimeout(() => {
        callReFetchStock();
      }, 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoSJ]);


  // Build sender / destination dropdown options from all counters instead of existing shipping data
  useEffect(() => {
    const counters = Array.isArray(countersFetched?.data)
      ? countersFetched.data
      : [];

    if (counters.length === 0) {
      return;
    }

    const counterCodes = counters
      .map((counter) => counter?.code)
      .filter(Boolean);

    const uniqueCodes = Array.from(new Set(counterCodes));

    setSenderOptions(uniqueCodes);
    setDestinationOptions(uniqueCodes);
  }, [countersFetched]);

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    // Reset to first page when searching
    dispatch({ type: "current", payload: 1 });
  };

  const handleStockTableChange = (page, pageSize) => {
    setStockPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  // Extract stock data from response
  // Backend returns: { data: [...], pagination: {...} }
  // axios wraps it: response.data = { data: [...], pagination: {...} }
  // useFetch returns the entire axios response, so stockFetched.data is the API response
  const allStockRows = useMemo(() => {
    // Return empty array if no shipping is selected or still loading
    if (!selectedNoSJ || loadingStock) {
      return [];
    }
    
    // Handle axios response structure
    // stockFetched is the axios response object: { data: {...}, status: 200, ... }
    // stockFetched.data is the API response: { data: [...], pagination: {...} }
    const apiResponse = stockFetched?.data;
    
    // If no response data, return empty array
    if (!apiResponse) {
      return [];
    }
    
    // Handle standard API response structure: { data: [...], pagination: {...} }
    if (apiResponse && typeof apiResponse === 'object') {
      // Check if apiResponse has a data property that is an array
      if ('data' in apiResponse && Array.isArray(apiResponse.data)) {
        return apiResponse.data;
      }
      
      // Check if apiResponse has a data property that is an object with a data array
      // (nested structure sometimes returned)
      if ('data' in apiResponse && 
          apiResponse.data && 
          typeof apiResponse.data === 'object' && 
          'data' in apiResponse.data && 
          Array.isArray(apiResponse.data.data)) {
        return apiResponse.data.data;
      }
    }
    
    // Fallback: if apiResponse itself is an array
    if (Array.isArray(apiResponse)) {
      return apiResponse;
    }
    
    // If we get here, the response structure is unexpected
    return [];
  }, [stockFetched, selectedNoSJ, loadingStock]);

  const stockTotal = allStockRows.length;

  const pagedStockRows = useMemo(() => {
    const current = stockPagination.current || 1;
    const size = stockPagination.pageSize || 10;
    const start = (current - 1) * size;
    const end = start + size;
    return allStockRows.slice(start, end);
  }, [allStockRows, stockPagination.current, stockPagination.pageSize]);

  const handleDownloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);

      const rowsToExport = filteredAndSortedRows;

      if (!Array.isArray(rowsToExport) || rowsToExport.length === 0) {
        message.warning("No data available to export");
        return;
      }

      // Format data for Excel export
      const formattedData = rowsToExport.map((item, index) => ({
        "No": index + 1,
        "No Form": item.noForm || "",
        "No SJ": item.noSJ || "",
        "Kirim Dari": item.sender || "",
        "Kirim Ke": item.destination || "",
        "Consignment Type": item.consignmentType || "",
        "Total Quantity": item.totalQuantity || 0,
        "Total Value": item.totalCost ? `Rp ${item.totalCost.toLocaleString("id-ID")}` : "-",
        "Status": item.status || "",
        "Keterangan": item.keterangan || "",
        "Tanggal Kirim": item.tanggal_kirim
          ? new Date(item.tanggal_kirim).toLocaleDateString("id-ID")
          : "",
        "Date Received": item.dateReceived 
          ? new Date(item.dateReceived).toLocaleDateString("id-ID")
          : "",
      }));

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 5 },   // No
        { wch: 15 },  // No Form
        { wch: 15 },  // No SJ
        { wch: 15 },  // Kirim Dari
        { wch: 15 },  // Kirim Ke
        { wch: 20 },  // Consignment Type
        { wch: 15 },  // Total Quantity
        { wch: 20 },  // Total Value
        { wch: 15 },  // Status
        { wch: 30 },  // Keterangan
        { wch: 25 },  // Date Received
      ];
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Shipping Data");
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Shipping_Data_${currentDate}.xlsx`;
      
      XLSX.writeFile(workbook, filename);
      message.success(
        `Excel file downloaded successfully with ${rowsToExport.length} records`
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
      message.error("Failed to download Excel file. Please try again.");
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    dispatch({ type: "current", payload: pagination.current });
    dispatch({ type: "pageSize", payload: pagination.pageSize });

    // Create a new filteredInfo object that preserves our existing filters
    const newFilteredInfo = { ...state.filteredInfo };

    // Process table filters and add them to our existing filters
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        // Make sure all filter values are arrays for backend compatibility
        newFilteredInfo[key] = Array.isArray(filters[key])
          ? filters[key]
          : [filters[key]];
      } else {
        // If a filter was cleared in the table, remove it from our state
        delete newFilteredInfo[key];
      }
    });

    // Preserve shipping type filter logic
    if (shippingTypeFilter) {
      // Don't add shippingType to filters since it doesn't exist in the model
      // Only use it to control sender and destination filters

      // Maintain the conditional filter logic based on shipping type
      if (shippingTypeFilter === "Return") {
        newFilteredInfo.destination = ["GP"];
        delete newFilteredInfo.sender;
      } else if (shippingTypeFilter === "Regular") {
        newFilteredInfo.sender = ["GP"];
        delete newFilteredInfo.destination;
      } else if (shippingTypeFilter === "Mutasi") {
        // For Mutasi: select all in sender and destination except for 'GP'
        const sendersExceptGP = senderOptions.filter(sender => sender !== "GP");
        const destinationsExceptGP = destinationOptions.filter(destination => destination !== "GP");
        
        newFilteredInfo.sender = sendersExceptGP;
        newFilteredInfo.destination = destinationsExceptGP;
      } else if (shippingTypeFilter === "Return Pabrik") {
        // For Return Pabrik: select all in sender except for 'GP', and select 'PRI' in destination
        const sendersExceptGP = senderOptions.filter(sender => sender !== "GP");
        
        newFilteredInfo.sender = sendersExceptGP;
        newFilteredInfo.destination = ["PRI"];
      }
    }

    // Update the filter state
    dispatch({ type: "filteredInfo", payload: newFilteredInfo });

    // Update the sorter state
    dispatch({ type: "sorter", payload: sorter });

    // Local pagination/filtering/sorting: no refetch needed here.
  };

  const filteredAndSortedRows = useMemo(() => {
    const keyword = state.searchKeyword;
    const senderSelected = state.filteredInfo?.sender;
    const destinationSelected = state.filteredInfo?.destination;
    const statusSelected = state.filteredInfo?.status;

    const dateFilter = state.filteredInfo?.tanggal_kirim;
    const startMs = dateFilter?.startDate ? toMs(dateFilter.startDate) : null;
    const endMs = dateFilter?.endDate ? toMs(dateFilter.endDate) : null;

    const base = Array.isArray(allShippingRows) ? allShippingRows : [];

    const filtered = base.filter((row) => {
      if (!matchesAny(row?.sender, senderSelected)) return false;
      if (!matchesAny(row?.destination, destinationSelected)) return false;
      if (!matchesAny(row?.status, statusSelected)) return false;

      if (startMs != null || endMs != null) {
        const rowMs = toMs(row?.tanggal_kirim);
        if (rowMs == null) return false;
        if (startMs != null && rowMs < startMs) return false;
        if (endMs != null && rowMs > endMs + 24 * 60 * 60 * 1000 - 1) return false;
      }

      if (!keyword) return true;

      const haystacks = [
        row?.noForm,
        row?.noSJ,
        row?.sender,
        row?.destination,
        row?.consignmentType,
        row?.status,
        row?.keterangan,
      ];

      return haystacks.some((v) => includesText(v, keyword));
    });

    const field = state.sorter?.field;
    const order = state.sorter?.order; // "ascend" | "descend"
    if (!field || !order) return filtered;

    const dir = order === "descend" ? -1 : 1;

    const sorted = [...filtered].sort((a, b) => {
      const av = a?.[field];
      const bv = b?.[field];

      const aNum = typeof av === "number" ? av : Number(av);
      const bNum = typeof bv === "number" ? bv : Number(bv);

      const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
      if (bothNumeric) return (aNum - bNum) * dir;

      const as = normalize(av);
      const bs = normalize(bv);
      if (as === bs) return 0;
      return as > bs ? dir : -dir;
    });

    return sorted;
  }, [allShippingRows, state.filteredInfo, state.searchKeyword, state.sorter]);

  const paginationTotal = filteredAndSortedRows.length;
  const currentPage = state.current || 1;
  const pageSize = state.pageSize || 10;

  const pagedShippingRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAndSortedRows.slice(start, end);
  }, [currentPage, filteredAndSortedRows, pageSize]);

  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Button type="primary" onClick={handleCreate}>
          Create New Shipping
        </Button>

        <Button 
          type="default" 
          onClick={handleDownloadExcel}
          loading={isDownloadingExcel}
          style={{ marginLeft: "8px" }}
        >
          {isDownloadingExcel ? "Downloading..." : "Download Excel"}
        </Button>

        <Select
          allowClear
          mode="multiple"
          style={{ minWidth: 200 }}
          placeholder="Sender"
          value={senderFilter}
          onChange={handleSenderFilter}
          maxTagCount={2}
          maxTagTextLength={10}
          disabled={shippingTypeFilter === "Regular"}
        >
          {senderOptions.map((sender) => (
            <Select.Option key={sender} value={sender}>
              {sender}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          mode="multiple"
          style={{ minWidth: 200 }}
          placeholder="Destination"
          value={destinationFilter}
          onChange={handleDestinationFilter}
          maxTagCount={2}
          maxTagTextLength={10}
        >
          {destinationOptions.map((destination) => (
            <Select.Option key={destination} value={destination}>
              {destination}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          style={{ minWidth: 140 }}
          placeholder="Shipping Type"
          value={shippingTypeFilter}
          onChange={handleShippingTypeFilter}
        >
          <Select.Option value="Regular">Regular</Select.Option>
          <Select.Option value="Return">Return</Select.Option>
          <Select.Option value="Mutasi">Mutasi</Select.Option>
          <Select.Option value="Return Pabrik">Return Pabrik</Select.Option>
        </Select>

        <RangePicker
          placeholder={["Start Date", "End Date"]}
          value={dateRange}
          onChange={handleDateRangeChange}
          style={{ minWidth: 240 }}
          format="YYYY-MM-DD"
        />

        <Search
          placeholder="Search..."
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
          onSearch={handleSearch}
          style={{ width: "300px" }}
        />
      </div>

      <ShippingTable
        history={history}
        loading={loading}
        userData={userData}
        data={pagedShippingRows}
        pagination={{
          current: currentPage,
          pageSize,
          total: paginationTotal,
        }}
        callReFetch={callReFetch}
        dispatch={dispatch}
        handleDelete={handleDelete}
        setSelectedNoSJ={setSelectedNoSJ}
        stockData={{
          data: pagedStockRows,
          pagination: {
            current: stockPagination.current,
            pageSize: stockPagination.pageSize,
            total: stockTotal,
          },
        }}
        callReFetchStock={callReFetchStock}
        loadingStock={loadingStock}
        onChange={handleTableChange}
        onStockPaginationChange={handleStockTableChange}
      />

      {/* <Modal
        title="Create Shipping"
        open={isModalVisible}
        footer={null}
        onCancel={handleCloseModal}
      >
        <ShippingCreationForm
          history={history}
          userData={userData}
          callReFetch={callReFetch}
          onClose={handleCloseModal}
        />
      </Modal> */}

      <Modal
        title="Delete Shipping"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <p>Are you sure you want to delete this shipping?</p>
      </Modal>
    </div>
  );
};

ShippingPage.propTypes = {};

export default ShippingPage;
